/*!
 * Bailey & Coco "Find your size": standalone product-page size finder. v2.0.0, 22 Oct 2026.
 * Built for the A/B test 2 challenger theme. LOCAL BUILD: not deployed anywhere.
 *
 * Scope (Andrew, 22 Oct 2026): product page only, beside the existing size selector and size guide. No quick-add sheet,
 * no bundle selector, no AOV layer. Never selects a size by itself and never changes any purchase button.
 * Harness products only (Trail & Glow chart, or the Adjustable Neck chart for products tagged adjustable-neck-harness).
 * The Liquid snippet snippets/bc-size-finder.liquid outputs nothing on any other product.
 *
 * Data sources (every number):
 *  - Published adjustment ranges ("adj"): live theme 178472157560 sections/product-detail.liquid,
 *    Trail & Glow = SIZE GUIDE MODAL harness table (lines 2390-2412, also the Size Guide tab at line 2162);
 *    Adjustable Neck = the adjustable_neck_sizeguide capture (line 979). Copy: tests/fixtures/reference/live-size-guide-excerpt.liquid
 *  - Best-fit bands ("best") and border zones ("zone"): CRO-COLLECTION-QUICKADD-2026-10-10/RESEARCH/best-fit-bands.json
 *    (version 2026-10-10-proposal; SIZE-CHART-BEST-FIT-DECISION.md section 3), byte-identical to the bands embedded in variant theme
 *    178881790328 assets/bc-fit-finder.js. Copy: tests/fixtures/reference/best-fit-bands.json
 *  - TOL 1 cm = best-fit-bands.json rule.tolerance_cm; GAP 3 cm = ROUND2/fit_rule.py ("never dead-end", Andrew 10 Oct 15:32).
 *  - Plausible input ranges (neck 12-90, chest 18-130 cm) = ROUND2/src/bc-fit.src.js PLAUS.
 *  - Breed table: variant theme 178881790328 assets/bc-fit-finder.js (built from RESEARCH/breed-table-proposed.csv + Loox reviews),
 *    regenerated by tools/build_breed_data.py (see that file for every change).
 *
 * Analytics: Microsoft Clarity custom events + tags, only if window.clarity already exists (Clarity runs under the theme's
 * consent bridge in layout/theme.liquid, exactly as the old finder relied on). Optional mirror to window.dataLayer only if it
 * already exists. No new vendor, no cookie of ours, no personal data. Event dictionary: EVENT-DICTIONARY.md.
 */
(function (root, factory) {
  'use strict';
  var api = factory(root);
  if (typeof module === 'object' && module.exports) { module.exports = api; return; }
  if (root && root.document) {
    if (root.BCSizeFinder && root.BCSizeFinder.version) return;
    root.BCSizeFinder = api;
    if (root.document.readyState === 'loading') root.document.addEventListener('DOMContentLoaded', api.initAll);
    else api.initAll();
  }
})(typeof window !== 'undefined' ? window : this, function (W) {
  'use strict';

  var VERSION = '2.0.0';
  var PROP_VERSION = '2';
  var TOL = 1, GAP = 3;
  var PLAUS = { neck: [12, 90], chest: [18, 130] };
  var STORE_PREFIX = 'bc_sf:v2:';
  var STORE_TTL_MS = 2 * 60 * 60 * 1000;   /* a saved answer is offered back on the same product for 2 hours */
  var MAX_QUERY_TAGS = 5;                  /* typed-text tags per product per page view */
  var ORDER = ['XXS', 'XS', 'S', 'M', 'L', 'XL'];

  /* ---------------------------------------------------------------------------------------------
     Size charts. adj = published adjustment range (live size guide). best/zone = best-fit bands.
     Bands are [from, to): a value equal to "to" belongs to the next size.
     --------------------------------------------------------------------------------------------- */
  var CHARTS = {
    trail_glow: {
      name: 'Trail & Glow harness',
      sizes: [
        { size: 'XXS', adj: { neck: [20, 29], chest: [22, 41] }, best: { chest: [22, 34], neck: [20, 25] }, zone: { chest: [33, 34], neck: [24, 25] } },
        { size: 'XS', adj: { neck: [24, 34], chest: [26, 50] }, best: { chest: [34, 45], neck: [25, 31] }, zone: { chest: [44, 45], neck: [30, 31] } },
        { size: 'S', adj: { neck: [30, 45], chest: [35, 66] }, best: { chest: [45, 54], neck: [31, 40] }, zone: { chest: [53, 54], neck: [39, 40] } },
        { size: 'M', adj: { neck: [35, 50], chest: [40, 71] }, best: { chest: [54, 68], neck: [40, 46] }, zone: { chest: [66, 68], neck: [45, 46] } },
        { size: 'L', adj: { neck: [40, 60], chest: [55, 83] }, best: { chest: [68, 80], neck: [46, 56] }, zone: { chest: [78, 80], neck: [55, 56] } },
        { size: 'XL', adj: { neck: [55, 83], chest: [71, 99] }, best: { chest: [80, 99], neck: [56, 83] }, zone: {} }
      ]
    },
    adjustable_neck: {
      name: 'Adjustable Neck harness',
      sizes: [
        { size: 'XXS', adj: { neck: [19, 25], chest: [28, 40] }, best: { chest: [28, 38], neck: [19, 25] }, zone: { chest: [37, 38] } },
        { size: 'XS', adj: { neck: [26, 40], chest: [35, 48] }, best: { chest: [38, 45], neck: [25, 38] }, zone: { chest: [44, 45], neck: [37, 38] } },
        { size: 'S', adj: { neck: [36, 46], chest: [41, 54] }, best: { chest: [45, 54], neck: [38, 46] }, zone: {} }
      ]
    }
  };

  /* BREED-DATA:START (generated by tools/build_breed_data.py; do not edit by hand) */
  var BREEDS = {"trail_glow":[{"n":"Dachshund","p":"Dachshunds","s":["XS","S"],"c":"M","a":["Sausage Dog","Daxie","Doxie","Wiener Dog","Teckel"],"t":["daschund","dachsund","dashund","daschshund","dauchsund","dachshound","daxhund","dachund"],"r":40,"tip":"Not sure if yours is a miniature or a standard? Miniature Dachshunds usually wear XS, so measuring is the best way to choose."},{"n":"Cockapoo","p":"Cockapoos","s":["S","M"],"c":"M","a":["Cockerpoo","Cockapoo toy/mini/standard","Spoodle","Cocker Poo","Cockapoodle","Cockerpoodle"],"t":["cockerpoo","cockapo"],"r":25,"tip":"Cockapoos range from toy to standard in size, so measure to choose between them."},{"n":"Cocker Spaniel","p":"Cocker Spaniels","s":["M"],"c":"H","a":["Cocker","English Cocker","Working Cocker","Show Cocker","Working Cocker Spaniel","Show Cocker Spaniel","English Cocker Spaniel"],"r":33,"tip":"Smaller working Cockers often wear S."},{"n":"Miniature Dachshund","p":"Miniature Dachshunds","s":["XS"],"c":"H","a":["Mini Dachshund","Mini Sausage Dog","Mini Dachsie","Miniature Long-haired/Smooth/Wire Dachshund"],"r":16,"tip":"Very small minis may need XXS and larger long-haired minis S."},{"n":"French Bulldog","p":"French Bulldogs","s":["M"],"c":"H","a":["Frenchie","Frenchy","French Bull Dog"],"r":28,"tip":"Very stocky adults may need L."},{"n":"Cavapoo","p":"Cavapoos","s":["S"],"c":"H","a":["Cavoodle","Cavapoodle","Cavipoo"],"r":16},{"n":"Labrador Retriever","p":"Labrador Retrievers","s":["L"],"c":"H","a":["Labrador","Lab","Fox Red Lab"],"t":["labrodor","labradore"],"r":17,"tip":"Smaller Labradors may wear M and larger ones XL."},{"n":"Staffordshire Bull Terrier","p":"Staffordshire Bull Terriers","s":["M","L"],"c":"M","a":["Staffy","Staffie","Staff","Staffordshire","SBT","Staffy Bull Terrier"],"r":5,"tip":"Staffies are compact and broad, so measure the chest."},{"n":"Jack Russell Terrier","p":"Jack Russell Terriers","s":["S"],"c":"M","a":["Jack Russell","JRT","Jack Russel","Russell","Jack Russel Terrier"],"t":["jack russel"],"r":2},{"n":"Golden Retriever","p":"Golden Retrievers","s":["L","XL"],"c":"M","a":["Golden","Goldie","Retriever"],"r":8,"tip":"Measure the chest to choose between them."},{"n":"Chihuahua","p":"Chihuahuas","s":["XXS","XS"],"c":"M","a":["Chi","Chiwawa","Chihuahua long coat/smooth"],"t":["chiuaua","chihuaha","chihuahau","chiwawa"],"r":4,"tip":"Deep-chested Chihuahuas may need XS."},{"n":"Yorkshire Terrier","p":"Yorkshire Terriers","s":["XXS","XS"],"c":"M","a":["Yorkie","Yorky","Yorkshire"],"r":2,"tip":"Smaller Yorkies may wear XXS."},{"n":"Shih Tzu","p":"Shih Tzus","s":["S"],"c":"M","a":["Shih-Tzu","Shitzu","Shihtzu"],"t":["shitzu","shih tsu","shitsu","shitzhu"],"r":3},{"n":"Pekingese","p":"Pekingese","s":["XS","S"],"c":"L","a":["Peke","Pekinese","Lion Dog"],"t":["pekingese","pekinese","pekenese","pekignese"],"r":0},{"n":"Pug","p":"Pugs","s":["M"],"c":"M","r":7,"tip":"Smaller Pugs may wear S."},{"n":"Border Collie","p":"Border Collies","s":["M","L"],"c":"L","a":["Collie"],"r":1},{"n":"English Bulldog","p":"English Bulldogs","s":["L","XL"],"c":"M","a":["British Bulldog","Bulldog"]},{"n":"English Springer Spaniel","p":"English Springer Spaniels","s":["M","L"],"c":"L","a":["Springer","Springer Spaniel","English Springer"],"r":3},{"n":"West Highland White Terrier","p":"West Highland White Terriers","s":["S","M"],"c":"M","a":["Westie","Westy","West Highland Terrier"],"r":5,"tip":"Westies are deep-chested for their size."},{"n":"Cavalier King Charles Spaniel","p":"Cavalier King Charles Spaniels","s":["S"],"c":"M","a":["Cavalier","King Charles","CKCS","Cavvy","King Charles Spaniel","Cavalier King Charles"],"r":2},{"n":"German Shepherd","p":"German Shepherds","s":["XL"],"c":"M","a":["GSD","Alsatian","German Shepherd Dog"],"r":1},{"n":"Border Terrier","p":"Border Terriers","s":["S"],"c":"M","a":["Border"],"r":2},{"n":"Miniature Schnauzer","p":"Miniature Schnauzers","s":["S"],"c":"M","a":["Mini Schnauzer","Schnauzer"]},{"n":"Bichon Frise","p":"Bichon Frises","s":["S"],"c":"M","a":["Bichon"],"r":1},{"n":"Beagle","p":"Beagles","s":["M"],"c":"M","r":3},{"n":"Labradoodle","p":"Labradoodles","s":["M","L"],"c":"L","a":["Labradoodle (mini/medium/standard)","Lab doodle","Australian Labradoodle"],"r":2,"tip":"Doodles vary from mini to standard in size."},{"n":"Pomeranian","p":"Pomeranians","s":["XXS","XS"],"c":"L","a":["Pom","Pommy"],"t":["pomerainian","pomeranean"],"tip":"Measure close to the body, under the coat."},{"n":"Toy Poodle","p":"Toy Poodles","s":["XS"],"c":"M","r":2},{"n":"Siberian Husky","p":"Siberian Huskies","s":["L"],"c":"M","a":["Husky","Huskies"],"r":1},{"n":"Lhasa Apso","p":"Lhasa Apsos","s":["S"],"c":"L","a":["Lhasa"],"tip":"Measure close to the body, under the coat."},{"n":"Boxer","p":"Boxers","s":["L","XL"],"c":"L"},{"n":"Sprocker","p":"Sprockers","s":["M"],"c":"M","a":["Sprocker Spaniel","Springer x Cocker"],"r":2},{"n":"Standard Dachshund","p":"Standard Dachshunds","s":["S","M"],"c":"L","a":["Standard Sausage Dog"]},{"n":"Whippet","p":"Whippets","s":["S","M"],"c":"L","tip":"Slim neck and deep chest, so measure both."},{"n":"Miniature Poodle","p":"Miniature Poodles","s":["S"],"c":"M","a":["Mini Poodle"]},{"n":"Lurcher","p":"Lurchers","s":[],"c":"L","a":["Greyhound cross"],"tip":"Slim neck and deep chest, so measure both."},{"n":"Maltese","p":"Maltese","s":["XS","S"],"c":"L","a":["Maltese Terrier"],"r":2},{"n":"Corgi","p":"Corgis","s":["M"],"c":"M","a":["Pembroke Corgi","Cardigan Corgi","Welsh Corgi"],"r":2},{"n":"Rottweiler","p":"Rottweilers","s":["XL"],"c":"M","a":["Rottie"],"r":3,"tip":"Sizes vary a lot in this breed, so measure the chest and neck."},{"n":"Greyhound","p":"Greyhounds","s":["L","XL"],"c":"L","a":["Retired greyhound","Grey"],"tip":"Slim neck and deep chest, so measure both."},{"n":"Dalmatian","p":"Dalmatians","s":["L"],"c":"M","a":["Dalmation","Dally"],"t":["dalmation"],"r":2},{"n":"Cavachon","p":"Cavachons","s":["S"],"c":"L","r":1},{"n":"Boston Terrier","p":"Boston Terriers","s":["S","M"],"c":"L","a":["Boston"]},{"n":"Patterdale Terrier","p":"Patterdale Terriers","s":[],"c":"L","a":["Patterdale"]},{"n":"Maltipoo","p":"Maltipoos","s":["XS","S"],"c":"L","a":["Maltipoodle"],"r":1},{"n":"Italian Greyhound","p":"Italian Greyhounds","s":["XS","S"],"c":"L","a":["Iggy","IG"],"tip":"Slim neck and deep chest, so measure both."},{"n":"Bull Terrier","p":"Bull Terriers","s":["L"],"c":"L","a":["English Bull Terrier","Miniature Bull Terrier"],"tip":"Miniature Bull Terriers are smaller, so please measure."},{"n":"Goldendoodle","p":"Goldendoodles","s":["L","XL"],"c":"L","a":["Golden doodle","Groodle"],"r":2,"tip":"Doodles vary from mini to standard in size."},{"n":"Hungarian Vizsla","p":"Hungarian Vizslas","s":["M","L"],"c":"L","a":["Vizsla"],"tip":"Slim neck and deep chest, so measure both."},{"n":"Standard Poodle","p":"Standard Poodles","s":["L"],"c":"L","a":["Poodle"],"r":4},{"n":"Dobermann","p":"Dobermanns","s":["XL"],"c":"L","a":["Doberman","Dobie"],"tip":"Sizes vary a lot in this breed, so measure the chest and neck."},{"n":"Shiba Inu","p":"Shiba Inus","s":["M"],"c":"L","a":["Shiba"]},{"n":"Bernese Mountain Dog","p":"Bernese Mountain Dogs","s":["XL"],"c":"L","a":["Bernese","Berner"],"r":2,"tip":"Sizes vary a lot in this breed, so measure the chest and neck."},{"n":"Cairn Terrier","p":"Cairn Terriers","s":["S"],"c":"L","a":["Cairn"]},{"n":"Shih-poo","p":"Shih-poos","s":[],"c":"L","a":["Shihpoo","Shoodle"]},{"n":"Jackapoo","p":"Jackapoos","s":[],"c":"L","a":["Jack-a-poo","Jackadoodle"]},{"n":"Pomsky","p":"Pomskies","s":[],"c":"L"},{"n":"Weimaraner","p":"Weimaraners","s":["L","XL"],"c":"L"},{"n":"Australian Shepherd","p":"Australian Shepherds","s":["M","L"],"c":"L","a":["Aussie"]},{"n":"Scottish Terrier","p":"Scottish Terriers","s":["M"],"c":"L","a":["Scottie"],"r":2},{"n":"Great Dane","p":"Great Danes","s":["XL"],"c":"L","a":["Dane"],"tip":"Sizes vary a lot in this breed, so measure the chest and neck."},{"n":"Chow Chow","p":"Chow Chows","s":["XL"],"c":"L","a":["Chow"],"r":1,"tip":"Measure close to the body, under the coat."},{"n":"Bearded Collie","p":"Bearded Collies","s":["M","L"],"c":"L","a":["Beardie","Beardy"],"t":["bearded colly","beard collie"],"tip":"Measure close to the body, under the coat."},{"n":"Kokoni","p":"Kokonis","s":[],"c":"L","a":["Greek Kokoni","Small Greek Domestic Dog"],"r":0}],"adjustable_neck":[{"n":"Chihuahua","p":"Chihuahuas","s":["XXS"],"c":"M","a":["Chi","Chiwawa","Chihuahua long coat/smooth"],"t":["chiuaua","chihuaha","chihuahau","chiwawa"],"tip":"Deep-chested Chihuahuas may need XS."},{"n":"Yorkshire Terrier","p":"Yorkshire Terriers","s":["XXS","XS"],"c":"M","a":["Yorkie","Yorky","Yorkshire"],"r":1,"tip":"Smaller Yorkies may wear XXS."},{"n":"Maltese","p":"Maltese","s":["XS"],"c":"L","a":["Maltese Terrier"]},{"n":"Toy Poodle","p":"Toy Poodles","s":["XS"],"c":"L"},{"n":"Pomeranian","p":"Pomeranians","s":["XXS","XS"],"c":"L","a":["Pom","Pommy"],"t":["pomerainian","pomeranean"],"tip":"Measure close to the body, under the coat."},{"n":"Miniature Dachshund","p":"Miniature Dachshunds","s":["XS"],"c":"M","a":["Mini Dachshund","Mini Sausage Dog","Mini Dachsie","Miniature Long-haired/Smooth/Wire Dachshund"],"tip":"Very small minis may need XXS and larger long-haired minis S."},{"n":"Cavapoo","p":"Cavapoos","s":["XS","S"],"c":"M","a":["Cavoodle","Cavapoodle","Cavipoo"],"r":4},{"n":"Shih Tzu","p":"Shih Tzus","s":["S"],"c":"L","a":["Shih-Tzu","Shitzu","Shihtzu"],"t":["shitzu","shih tsu","shitsu","shitzhu"]},{"n":"Pekingese","p":"Pekingese","s":["XS","S"],"c":"L","a":["Peke","Pekinese","Lion Dog"],"t":["pekingese","pekinese","pekenese","pekignese"],"r":0},{"n":"Bichon Frise","p":"Bichon Frises","s":["S"],"c":"L","a":["Bichon"]},{"n":"Jack Russell Terrier","p":"Jack Russell Terriers","s":["S"],"c":"L","a":["Jack Russell","JRT","Jack Russel","Russell","Jack Russel Terrier"],"t":["jack russel"]},{"n":"Miniature Poodle","p":"Miniature Poodles","s":["S"],"c":"L","a":["Mini Poodle"]},{"n":"Miniature Schnauzer","p":"Miniature Schnauzers","s":["S"],"c":"L","a":["Mini Schnauzer","Schnauzer"]},{"n":"Cavalier King Charles Spaniel","p":"Cavalier King Charles Spaniels","s":["S"],"c":"L","a":["Cavalier","King Charles","CKCS","Cavvy","King Charles Spaniel","Cavalier King Charles"]},{"n":"Maltipoo","p":"Maltipoos","s":["XS","S"],"c":"L","a":["Maltipoodle"]},{"n":"Italian Greyhound","p":"Italian Greyhounds","s":["XS","S"],"c":"L","a":["Iggy","IG"],"tip":"Slim neck and deep chest, so measure both."},{"n":"Border Terrier","p":"Border Terriers","s":["S"],"c":"L","a":["Border"]},{"n":"Kokoni","p":"Kokonis","s":[],"c":"L","a":["Greek Kokoni","Small Greek Domestic Dog"],"r":0},{"n":"Dachshund","p":"Dachshunds","s":[],"c":"M","a":["Sausage Dog","Daxie","Doxie","Wiener Dog","Teckel"],"t":["daschund","dachsund","dashund","daschshund","dauchsund","dachshound","daxhund","dachund"],"g":0,"r":40},{"n":"Cockapoo","p":"Cockapoos","s":[],"c":"M","a":["Cockerpoo","Cockapoo toy/mini/standard","Spoodle","Cocker Poo","Cockapoodle","Cockerpoodle"],"t":["cockerpoo","cockapo"],"g":0,"r":25},{"n":"Cocker Spaniel","p":"Cocker Spaniels","s":[],"c":"H","a":["Cocker","English Cocker","Working Cocker","Show Cocker","Working Cocker Spaniel","Show Cocker Spaniel","English Cocker Spaniel"],"g":0,"r":33},{"n":"French Bulldog","p":"French Bulldogs","s":[],"c":"H","a":["Frenchie","Frenchy","French Bull Dog"],"g":0,"r":28},{"n":"Labrador Retriever","p":"Labrador Retrievers","s":[],"c":"H","a":["Labrador","Lab","Fox Red Lab"],"t":["labrodor","labradore"],"g":0,"r":17},{"n":"Staffordshire Bull Terrier","p":"Staffordshire Bull Terriers","s":[],"c":"M","a":["Staffy","Staffie","Staff","Staffordshire","SBT","Staffy Bull Terrier"],"g":0,"r":5},{"n":"Golden Retriever","p":"Golden Retrievers","s":[],"c":"M","a":["Golden","Goldie","Retriever"],"g":0,"r":8},{"n":"Pug","p":"Pugs","s":[],"c":"M","g":0,"r":7},{"n":"Border Collie","p":"Border Collies","s":[],"c":"L","a":["Collie"],"g":0,"r":1},{"n":"English Bulldog","p":"English Bulldogs","s":[],"c":"M","a":["British Bulldog","Bulldog"],"g":0},{"n":"English Springer Spaniel","p":"English Springer Spaniels","s":[],"c":"L","a":["Springer","Springer Spaniel","English Springer"],"g":0,"r":3},{"n":"West Highland White Terrier","p":"West Highland White Terriers","s":[],"c":"M","a":["Westie","Westy","West Highland Terrier"],"g":0,"r":5},{"n":"German Shepherd","p":"German Shepherds","s":[],"c":"M","a":["GSD","Alsatian","German Shepherd Dog"],"g":0,"r":1},{"n":"Beagle","p":"Beagles","s":[],"c":"M","g":0,"r":3},{"n":"Labradoodle","p":"Labradoodles","s":[],"c":"L","a":["Labradoodle (mini/medium/standard)","Lab doodle","Australian Labradoodle"],"g":0,"r":2},{"n":"Siberian Husky","p":"Siberian Huskies","s":[],"c":"M","a":["Husky","Huskies"],"g":0,"r":1},{"n":"Lhasa Apso","p":"Lhasa Apsos","s":[],"c":"L","a":["Lhasa"],"g":0},{"n":"Boxer","p":"Boxers","s":[],"c":"L","g":0},{"n":"Sprocker","p":"Sprockers","s":[],"c":"M","a":["Sprocker Spaniel","Springer x Cocker"],"g":0,"r":2},{"n":"Standard Dachshund","p":"Standard Dachshunds","s":[],"c":"L","a":["Standard Sausage Dog"],"g":0},{"n":"Whippet","p":"Whippets","s":[],"c":"L","g":0},{"n":"Lurcher","p":"Lurchers","s":[],"c":"L","a":["Greyhound cross"],"g":0},{"n":"Corgi","p":"Corgis","s":[],"c":"M","a":["Pembroke Corgi","Cardigan Corgi","Welsh Corgi"],"g":0,"r":2},{"n":"Rottweiler","p":"Rottweilers","s":[],"c":"M","a":["Rottie"],"g":0,"r":3},{"n":"Greyhound","p":"Greyhounds","s":[],"c":"L","a":["Retired greyhound","Grey"],"g":0},{"n":"Dalmatian","p":"Dalmatians","s":[],"c":"M","a":["Dalmation","Dally"],"t":["dalmation"],"g":0,"r":2},{"n":"Cavachon","p":"Cavachons","s":[],"c":"L","g":0,"r":1},{"n":"Boston Terrier","p":"Boston Terriers","s":[],"c":"L","a":["Boston"],"g":0},{"n":"Patterdale Terrier","p":"Patterdale Terriers","s":[],"c":"L","a":["Patterdale"],"g":0},{"n":"Bull Terrier","p":"Bull Terriers","s":[],"c":"L","a":["English Bull Terrier","Miniature Bull Terrier"],"g":0},{"n":"Goldendoodle","p":"Goldendoodles","s":[],"c":"L","a":["Golden doodle","Groodle"],"g":0,"r":2},{"n":"Hungarian Vizsla","p":"Hungarian Vizslas","s":[],"c":"L","a":["Vizsla"],"g":0},{"n":"Standard Poodle","p":"Standard Poodles","s":[],"c":"L","a":["Poodle"],"g":0,"r":4},{"n":"Dobermann","p":"Dobermanns","s":[],"c":"L","a":["Doberman","Dobie"],"g":0},{"n":"Shiba Inu","p":"Shiba Inus","s":[],"c":"L","a":["Shiba"],"g":0},{"n":"Bernese Mountain Dog","p":"Bernese Mountain Dogs","s":[],"c":"L","a":["Bernese","Berner"],"g":0,"r":2},{"n":"Cairn Terrier","p":"Cairn Terriers","s":[],"c":"L","a":["Cairn"],"g":0},{"n":"Shih-poo","p":"Shih-poos","s":[],"c":"L","a":["Shihpoo","Shoodle"],"g":0},{"n":"Jackapoo","p":"Jackapoos","s":[],"c":"L","a":["Jack-a-poo","Jackadoodle"],"g":0},{"n":"Pomsky","p":"Pomskies","s":[],"c":"L","g":0},{"n":"Weimaraner","p":"Weimaraners","s":[],"c":"L","g":0},{"n":"Australian Shepherd","p":"Australian Shepherds","s":[],"c":"L","a":["Aussie"],"g":0},{"n":"Scottish Terrier","p":"Scottish Terriers","s":[],"c":"L","a":["Scottie"],"g":0,"r":2},{"n":"Great Dane","p":"Great Danes","s":[],"c":"L","a":["Dane"],"g":0},{"n":"Chow Chow","p":"Chow Chows","s":[],"c":"L","a":["Chow"],"g":0,"r":1},{"n":"Bearded Collie","p":"Bearded Collies","s":[],"c":"L","a":["Beardie","Beardy"],"t":["bearded colly","beard collie"],"g":0}],"chips":{"trail_glow":["Cockapoo","Cocker Spaniel","Miniature Dachshund","French Bulldog","Cavapoo","Labrador Retriever"],"adjustable_neck":["Miniature Dachshund","Cavapoo","Yorkshire Terrier","Chihuahua"]}};
  /* BREED-DATA:END */

  /* =============================================================================================
     ENGINE (pure functions, unit-tested in Node by tests/engine.test.js)
     ============================================================================================= */

  var STOP = { dog: 1, dogs: 1, puppy: 1, puppies: 1, pup: 1, pups: 1, my: 1, the: 1, a: 1, an: 1, breed: 1, type: 1, mix: 1, mixed: 1, size: 1, is: 1, of: 1 };
  var EQUIV = { miniature: 'mini', minature: 'mini', miniture: 'mini', minitaure: 'mini' };

  function fold(s) {
    s = String(s == null ? '' : s).toLowerCase();
    if (s.normalize) s = s.normalize('NFD').replace(/[̀-ͯ]/g, '');
    return s.replace(/[^a-z]+/g, ' ').replace(/\s+/g, ' ').trim();
  }
  function stem(w) {
    w = EQUIV[w] || w;
    if (w.length > 3) {
      if (/ies$/.test(w)) w = w.slice(0, -3) + 'y';
      else if (/ie$/.test(w)) w = w.slice(0, -2) + 'y';
      else if (/[^s]s$/.test(w)) w = w.slice(0, -1);
    }
    return w;
  }
  function prep(s) {
    var toks = fold(s).split(' ').filter(function (w) { return w && !STOP[w]; }).map(stem);
    return { toks: toks, c: toks.join('') };
  }
  function lev(a, b) {
    var m = a.length, n = b.length, d = [], i, j;
    if (!m) return n; if (!n) return m;
    for (i = 0; i <= m; i++) d[i] = [i];
    for (j = 1; j <= n; j++) d[0][j] = j;
    for (i = 1; i <= m; i++) for (j = 1; j <= n; j++) {
      d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
    }
    return d[m][n];
  }
  function allow(len) { return len >= 8 ? 2 : (len >= 4 ? 1 : 0); }

  var TERM_CACHE = typeof WeakMap === 'function' ? new WeakMap() : null;
  function termsOf(e) {
    var t = TERM_CACHE && TERM_CACHE.get(e);
    if (t) return t;
    t = [];
    function add(raw, kind) { var p = prep(raw); if (p.c) t.push({ raw: raw, kind: kind, toks: p.toks, c: p.c }); }
    add(e.n, 'name');
    (e.a || []).forEach(function (x) { add(x, 'alias'); });
    (e.t || []).forEach(function (x) { add(x, 'typo'); });
    if (TERM_CACHE) TERM_CACHE.set(e, t);
    return t;
  }

  /* score one term against a prepared query: { s: 0-100, tier: 'full' | 'prefix' } or null */
  function scoreTerm(q, t) {
    var pen = t.kind === 'typo' ? 4 : 0;
    if (q.c === t.c) return { s: 100 - pen, tier: 'full' };
    var qt = q.toks, tt = t.toks, i, j;
    /* token matching: exact first, then fuzzy */
    var usedT = [], qFull = [], fuzzy = 0;
    for (i = 0; i < qt.length; i++) {
      for (j = 0; j < tt.length; j++) if (!usedT[j] && tt[j] === qt[i]) { usedT[j] = 1; qFull[i] = 1; break; }
    }
    for (i = 0; i < qt.length; i++) {
      if (qFull[i] || qt[i].length < 4) continue;
      for (j = 0; j < tt.length; j++) if (!usedT[j] && tt[j].length >= 4 && tt[j].indexOf(qt[i]) !== 0 && lev(qt[i], tt[j]) <= allow(qt[i].length)) { usedT[j] = 1; qFull[i] = 1; fuzzy++; break; }
    }
    var nQ = 0, nT = 0;
    for (i = 0; i < qt.length; i++) if (qFull[i]) nQ++;
    for (j = 0; j < tt.length; j++) if (usedT[j]) nT++;
    if (qt.length && nQ === qt.length) return { s: 90 - 2 * (tt.length - nT) - 5 * fuzzy - pen, tier: 'full', fz: fuzzy > 0 };          /* query inside term */
    var longTok = tt.some(function (w) { return w.length >= 3; });
    if (tt.length && nT === tt.length && longTok) return { s: 80 - 4 * (qt.length - nQ) - 5 * fuzzy - pen, tier: 'full', fz: fuzzy > 0 }; /* term inside query */
    /* incomplete typing: compact prefix, or last token is a prefix with everything before it matched */
    if (q.c.length >= 2 && t.c.indexOf(q.c) === 0) return { s: 75 + (q.c.length >= 6 ? 4 : 0) - pen, tier: 'prefix' };
    var last = qt.length - 1;
    if (last >= 0 && nQ === last && qt[last].length >= 2) {
      for (j = 0; j < tt.length; j++) if (!usedT[j] && tt[j].indexOf(qt[last]) === 0) return { s: 70 - pen, tier: 'prefix' };
    }
    if (q.c.length >= 5 && t.c.indexOf(q.c) !== 0 && lev(q.c, t.c) <= allow(q.c.length)) return { s: 60 - pen, tier: 'full', fz: true };
    if (q.c.length >= 4 && t.c.indexOf(q.c) > 0) return { s: 62 - pen, tier: 'full' };
    if (q.c.length >= 5 && lev(q.c, t.c.slice(0, q.c.length)) <= 1) return { s: 55 - pen, tier: 'prefix', fz: true };
    return null;
  }

  function search(list, raw, limit) {
    var q = prep(raw), res = [];
    if (!q.c) return res;
    (list || []).forEach(function (e, rank) {
      var best = null;
      termsOf(e).forEach(function (t) {
        var r = scoreTerm(q, t);
        if (r && (!best || r.s > best.s || (r.s === best.s && t.kind === 'name'))) best = { s: r.s, tier: r.tier, via: t.kind, term: t.raw, fz: !!r.fz };
      });
      if (best) res.push({ e: e, s: best.s, tier: best.tier, via: best.via, term: best.term, fz: best.fz, rank: rank });
    });
    res.sort(function (a, b) { return b.s - a.s || a.rank - b.rank; });
    if (res.length && res[0].s >= 96) res = res.filter(function (r) { return !r.fz; });          /* exact hit: drop typo-distance guesses */
    else if (res.length && res[0].s >= 60) res = res.filter(function (r) { return r.s >= 55; });
    return res.slice(0, limit || 6);
  }

  /* committed-query classification: match | incomplete | no_match | empty */
  function classify(list, raw) {
    var letters = fold(raw).replace(/ /g, '').length;
    if (!letters) return { result: 'empty', matches: [] };
    var m = search(list, raw);
    if (m.length && m[0].tier === 'full' && (letters >= 3 || m[0].s >= 96)) return { result: 'match', matches: m };
    if (letters < 3) return { result: 'incomplete', reason: 'short', matches: m };
    if (m.length && m[0].tier === 'prefix') return { result: 'incomplete', reason: 'prefix', matches: m };
    if (m.length) return { result: 'match', matches: m };
    return { result: 'no_match', matches: [] };
  }

  /* typed text for analytics: letters and spaces only, max 40 chars; dropped if it looks like contact details */
  function sanitizeQuery(raw) {
    var s = String(raw == null ? '' : raw);
    if (/@|\d{3,}|www\.|https?:/i.test(s)) return '[redacted]';
    s = s.toLowerCase();
    if (s.normalize) s = s.normalize('NFD').replace(/[̀-ͯ]/g, '');
    s = s.replace(/[^a-z ]+/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 40).trim();
    return s;
  }

  function sizesOf(chartKey) { var c = CHARTS[chartKey]; return c ? c.sizes.map(function (x) { return x.size; }) : []; }
  function bandIdx(L, d, v) {
    for (var i = 0; i < L.length; i++) { var b = L[i].best[d]; if (v >= b[0] && v < b[1]) return i; }
    return v < L[0].best[d][0] ? 0 : L.length - 1;
  }
  function borderUp(L, i, d, v) { var z = L[i].zone[d]; return !!(z && i < L.length - 1 && v >= z[0] && v < z[1]); }
  function off(L, i, d, v) { var a = L[i].adj[d]; return v < a[0] ? a[0] - v : (v > a[1] ? v - a[1] : 0); }
  function fitsAdj(L, i, d, v) { var a = L[i].adj[d]; return v >= a[0] - TOL && v <= a[1] + TOL; }
  function worstOff(L, i, neck, chest) { return Math.max(off(L, i, 'neck', neck), off(L, i, 'chest', chest)); }

  /* The measurement rule that ran in test 1 (variant theme bc-fit-finder.js / ROUND2/src/bands.src.js), ported unchanged:
     chest drives; a bigger neck moves the size up; border zone sizes up; neck too small steps down while the chest still fits. */
  function baseRule(L, neck, chest) {
    var ci = bandIdx(L, 'chest', chest), ni = bandIdx(L, 'neck', neck), i = ci, drive = 'chest', between = false, stepped = false;
    if (ni > ci) { i = ni; drive = 'neck'; }
    if (borderUp(L, i, drive, drive === 'neck' ? neck : chest)) { between = true; i++; }
    if (neck < L[i].adj.neck[0] - TOL) {
      var bestI = i, bo = off(L, i, 'neck', neck);
      for (var j = i - 1; j >= 0; j--) {
        if (!fitsAdj(L, j, 'chest', chest)) break;
        var o = off(L, j, 'neck', neck);
        if (o < bo) { bestI = j; bo = o; }
        if (o <= TOL) break;
      }
      if (bestI !== i) { i = bestI; between = false; stepped = true; }
    }
    return { i: i, ci: ci, ni: ni, between: between, stepped: stepped };
  }

  /* measurement -> suggestion. offered = size values this product actually sells (optional). */
  function measure(chartKey, neck, chest, offered) {
    var C = CHARTS[chartKey];
    if (!C || !isFinite(neck) || !isFinite(chest)) return null;
    var L = C.sizes, b = baseRule(L, neck, chest), i = b.i, sub = null;
    var sells = function (k) { return !offered || !offered.length || offered.indexOf(L[k].size) > -1; };
    if (!sells(i)) {
      var pick = -1, bd = 99;
      for (var k = 0; k < L.length; k++) if (sells(k)) { var dd = Math.abs(k - i); if (dd < bd || (dd === bd && k > i)) { pick = k; bd = dd; } }
      if (pick < 0) return null;
      sub = { from: L[i].size, dir: pick > i ? 'up' : 'down' };
      i = pick;
    }
    var worst = worstOff(L, i, neck, chest);
    var kind = worst <= TOL ? 'fit' : (worst <= GAP ? 'closest' : 'help');
    var alt = -1, spread = Math.abs(b.ci - b.ni);
    var shape = b.stepped && spread >= 2 ? 'deep_chest' : null;          /* deep chest, slim neck: the neck-led step down */
    var mismatch = spread >= 2 && !b.stepped;                           /* neck 2+ sizes above the chest: ask to re-measure */
    if (kind === 'fit' && !sub) {
      if (mismatch) kind = 'check';
      else if (spread <= 1) {
        /* borderline = the driving measurement sits in its size's published border zone, or either measurement is exactly
           on a band boundary, or chest and neck point to neighbouring sizes. The smaller neighbour must also adjust to both
           measurements, and the suggestion is always the larger size (the live size guide: "Between sizes? Size up"). */
        var drive = b.ni > b.ci ? 'neck' : 'chest', dv = drive === 'neck' ? neck : chest, dk = drive === 'neck' ? b.ni : b.ci;
        var cand = {}, z = L[dk].zone[drive];
        if (z && dk < L.length - 1 && dv >= z[0] && dv < z[1]) { cand[dk] = 1; cand[dk + 1] = 1; }
        if (b.ci > 0 && chest === L[b.ci].best.chest[0]) { cand[b.ci - 1] = 1; cand[b.ci] = 1; }
        if (b.ni > 0 && neck === L[b.ni].best.neck[0]) { cand[b.ni - 1] = 1; cand[b.ni] = 1; }
        if (spread === 1) { cand[b.ci] = 1; cand[b.ni] = 1; }
        if (cand[i] && cand[i - 1] && i > 0 && sells(i - 1) && worstOff(L, i - 1, neck, chest) <= TOL) alt = i - 1;
        if (alt > -1) kind = 'between';
      }
    }
    var sizes = alt > -1 ? [L[Math.min(i, alt)].size, L[Math.max(i, alt)].size] : [L[i].size];
    var ranges = {};
    sizes.forEach(function (s) { L.forEach(function (row) { if (row.size === s) ranges[s] = { chest: row.adj.chest.slice(), neck: row.adj.neck.slice() }; }); });
    return {
      method: 'measure', chart: chartKey, neck: neck, chest: chest, kind: kind, size: L[i].size, sizes: sizes,
      alt: alt > -1 ? L[alt].size : null, borderline: alt > -1, shape: shape, mismatch: mismatch, substituted: sub, ranges: ranges,
      neckOff: off(L, i, 'neck', neck), chestOff: off(L, i, 'chest', chest), first: L[0].size, last: L[L.length - 1].size
    };
  }

  /* breed -> suggestion (the breed table's sizes, limited to sizes this product sells) */
  function breedRec(chartKey, e, offered) {
    if (!e) return null;
    var res = { method: 'breed', chart: chartKey, breed: e.n, plural: e.p || e.n, conf: e.c, tip: e.tip || '', sizes: [], size: '', borderline: false, kind: 'measure_only', substituted: null, dropped: [] };
    if (e.g === 0) { res.kind = 'no_guide'; return res; }
    var sizes = (e.s || []).slice();
    if (!sizes.length) return res;
    if (offered && offered.length) {
      var kept = sizes.filter(function (s) { return offered.indexOf(s) > -1; });
      res.dropped = sizes.filter(function (s) { return offered.indexOf(s) < 0; });
      if (!kept.length) {
        var order = sizesOf(chartKey), want = order.indexOf(sizes[0]), pick = null, bd = 99;
        offered.forEach(function (s) { var k = order.indexOf(s); if (k < 0) return; var d = Math.abs(k - want); if (d < bd) { bd = d; pick = s; } });
        if (!pick) return res;
        res.substituted = { from: sizes.join('/'), dir: order.indexOf(pick) > want ? 'up' : 'down' };
        kept = [pick];
      }
      sizes = kept;
    }
    res.sizes = sizes;
    res.size = sizes.length === 1 ? sizes[0] : '';
    res.borderline = sizes.length > 1;
    res.kind = sizes.length > 1 ? 'two_sizes' : 'breed';
    return res;
  }

  function parseCm(raw) {
    var s = String(raw == null ? '' : raw).trim().toLowerCase().replace(/\s*(cm|cms|centimetres?|centimeters?)\.?$/, '').replace(',', '.').trim();
    if (!s) return { ok: false, err: 'empty' };
    if (!/^\d{1,3}(\.\d{1,2})?$/.test(s)) return { ok: false, err: 'nan' };
    return { ok: true, v: parseFloat(s) };
  }
  function checkMeasure(dim, raw) {
    var p = parseCm(raw);
    if (!p.ok) return p;
    if (p.v < PLAUS[dim][0] || p.v > PLAUS[dim][1]) return { ok: false, err: 'range', v: p.v };
    return p;
  }
  function list(arr) { return arr.length > 1 ? arr.slice(0, -1).join(', ') + ' or ' + arr[arr.length - 1] : (arr[0] || ''); }
  function cmRange(r) { return r[0] + ' to ' + r[1] + ' cm'; }
  function fmtCm(v) { return String(Math.round(v * 10) / 10); }

  /* =============================================================================================
     COPY. British English, no em dashes, "dog parent", never a gender, never a fit guarantee,
     never "too big / too small / won't fit", no promotions. Scanned by tests/engine.test.js.
     ============================================================================================= */
  var T = {
    title: 'Find your size for this harness',
    close: 'Close',
    closeLabel: 'Close size finder',
    tablist: 'How would you like to find your size?',
    tabBreed: 'By breed',
    tabMeasure: 'By measurements',
    breedLabel: "Your dog's breed",
    breedPlaceholder: 'For example, Cockapoo',
    breedHint: 'Start typing, then choose your breed from the list.',
    listLabel: 'Matching breeds',
    popular: 'Popular breeds',
    alsoCalled: 'Also called {term}',
    didYouMean: 'Closest match',
    measurePill: 'Measure',
    measureInstead: 'Crossbreed or not listed? Measure instead',
    keepTyping: 'Keep typing to see matching breeds.',
    noMatch: 'We don’t have a breed guide for “{q}” yet. Measure your dog’s chest and neck and we’ll suggest a size.',
    noMatchBtn: 'Measure instead',
    suggested: 'Our suggested size',
    sizeOr: '{a} or {b}',
    mostWear: 'Most {plural} wear {sizes} in this harness.',
    chooseBetween: 'Measure your dog’s chest and neck to choose between them.',
    lowConf: 'We have fewer reviews for this breed, so measuring is especially helpful.',
    measurePrompt: 'To be sure, measure your dog’s chest and neck.',
    measureBtn: 'Check with measurements',
    measureOnly: 'Sizes vary a lot for {plural}, so measuring is the best way to choose.',
    noGuide: 'We don’t have a breed guide for {plural} in this style yet. Measure your dog’s chest and neck and we’ll suggest the closest size.',
    measureMyDog: 'Measure my dog',
    startsAt: 'This design starts at {size}.',
    goesTo: 'This design goes up to {size}.',
    breedNotOffered: 'Most {plural} wear {sizes} in our harnesses. {range} Please measure your dog’s chest and neck to check {size}.',
    legend: 'Your dog’s measurements in cm',
    chest: 'Chest (cm)',
    chestHelp: 'Around the widest part of the chest, just behind the front legs.',
    neck: 'Neck (cm)',
    neckHelp: 'Around the base of the neck, where a collar sits.',
    howTitle: 'How to measure',
    how: [
      'Use a soft tape measure, or a piece of string and a ruler.',
      'Chest: go all the way round the widest part, just behind the front legs.',
      'Neck: go round the base of the neck, where a collar would sit.',
      'Keep the tape snug but not tight. You should fit two fingers underneath.',
      'Measure in centimetres. If your tape shows inches, multiply by 2.54.',
      'For fluffy coats, measure close to the body.'
    ],
    show: 'Show my size',
    errEmpty: { chest: 'Enter your dog’s chest measurement in cm.', neck: 'Enter your dog’s neck measurement in cm.' },
    errNan: 'Enter a number in cm, for example 52.',
    errRange: { chest: 'That chest measurement looks unusual. Please measure again in cm.', neck: 'That neck measurement looks unusual. Please measure again in cm.' },
    errSummary: 'Please check the highlighted measurement.',
    fitText: '{size} adjusts from {chest} around the chest and {neck} around the neck.',
    deepChest: 'Your dog’s chest is deep for their neck, so we suggest the size whose neck straps adjust to both measurements.',
    betweenTitle: 'Your dog is between {a} and {b}',
    betweenText: 'These measurements sit at the boundary between two sizes, and both adjust to them. Our suggested size is {size}, as our size guide advises sizing up when between sizes. Measure your dog’s chest and neck once more to confirm.',
    checkText: 'Your chest and neck measurements point to different sizes. Please measure both again. Based on these numbers, our suggested size is {size}.',
    mismatch: 'Your chest and neck measurements point to different sizes, so please measure both again.',
    closestTitle: 'Our closest size',
    closestText: 'Please measure your dog’s chest and neck again to check. If you would like a hand choosing, our customer team is happy to help.',
    rangeLine: '{size}: chest {chest}, neck {neck}',
    useSize: 'Use size {size}',
    soldOut: '(currently sold out)',
    selected: 'Size {size} selected',
    selectedStatus: 'Size {size} is now selected in the size options above. You can change it at any time.',
    resultStatus: 'Our suggested size is {sizes}.',
    restored: 'Your earlier answer for this harness',
    toggleRestored: 'Your earlier answer: {sizes}'
  };
  function t(key, v) {
    var s = T[key];
    return String(s == null ? '' : s).replace(/\{(\w+)\}/g, function (m, k) { return v && v[k] != null ? v[k] : m; });
  }

  /* =============================================================================================
     ANALYTICS: Clarity events/tags (only if Clarity is on the page) + optional dataLayer mirror.
     ============================================================================================= */
  var LOG = [];
  function track(name, tags, opts) {
    var copy = {};
    for (var k in (tags || {})) if (tags[k] != null && tags[k] !== '') copy[k] = String(tags[k]);
    LOG.push({ name: name, tags: copy, at: Date.now() });
    if (LOG.length > 300) LOG.shift();
    if (!W || !W.document) return;
    setTimeout(function () {
      try {
        var c = W.clarity;
        if (typeof c === 'function') {
          for (var k2 in copy) c('set', k2, copy[k2]);
          c('event', name);
        }
      } catch (e) { /* never break the page */ }
      try {
        var dl = W.dataLayer;
        if (opts && opts.dataLayer && dl && typeof dl.push === 'function') {
          var o = { event: name };
          for (var k3 in copy) o[k3] = copy[k3];
          dl.push(o);
        }
      } catch (e2) { /* never break the page */ }
    }, 0);
  }

  /* =============================================================================================
     STORAGE (sessionStorage, keyed by product id AND chart; nothing personal)
     ============================================================================================= */
  function storeKey(pid, chart) { return STORE_PREFIX + pid + ':' + chart; }
  function storeGet(pid, chart) {
    try {
      var raw = W.sessionStorage.getItem(storeKey(pid, chart));
      if (!raw) return null;
      var o = JSON.parse(raw);
      if (!o || o.pid !== pid || o.chart !== chart || !o.rec || Date.now() - (o.at || 0) > STORE_TTL_MS) {
        W.sessionStorage.removeItem(storeKey(pid, chart));
        return null;
      }
      return o;
    } catch (e) { return null; }
  }
  function storeSet(pid, chart, obj) {
    try {
      if (!obj) { W.sessionStorage.removeItem(storeKey(pid, chart)); return; }
      obj.pid = pid; obj.chart = chart; obj.at = Date.now();
      W.sessionStorage.setItem(storeKey(pid, chart), JSON.stringify(obj));
    } catch (e) { /* storage blocked: the finder still works for this page view */ }
  }
  function storePurge() {
    try {
      var s = W.sessionStorage, dead = [];
      for (var i = 0; i < s.length; i++) {
        var k = s.key(i);
        if (k && k.indexOf(STORE_PREFIX) === 0) {
          try { var o = JSON.parse(s.getItem(k)); if (!o || Date.now() - (o.at || 0) > STORE_TTL_MS) dead.push(k); } catch (e) { dead.push(k); }
        }
      }
      dead.forEach(function (k) { s.removeItem(k); });
    } catch (e) { /* ignore */ }
  }

  /* =============================================================================================
     UI
     ============================================================================================= */
  var OPENED = {};   /* product ids opened in this page view (bc_sf_open once per page view per product) */
  var uid = 0;

  function h(tag, attrs, kids) {
    var el = W.document.createElement(tag);
    for (var k in (attrs || {})) {
      var v = attrs[k];
      if (v == null || v === false) continue;
      if (k === 'text') el.textContent = v;
      else if (k === 'class') el.className = v;
      else if (k.indexOf('on') === 0 && typeof v === 'function') el.addEventListener(k.slice(2), v);
      else el.setAttribute(k, v === true ? '' : v);
    }
    (kids || []).forEach(function (c) { if (c != null) el.appendChild(typeof c === 'string' ? W.document.createTextNode(c) : c); });
    return el;
  }
  function norm(s) { return String(s == null ? '' : s).replace(/\s+/g, ' ').trim().toUpperCase(); }
  function coarse() { try { return !!(W.matchMedia && W.matchMedia('(pointer: coarse)').matches); } catch (e) { return false; } }
  function focusEl(el) { if (!el) return; try { el.focus({ preventScroll: false }); } catch (e) { try { el.focus(); } catch (e2) { /* ignore */ } } }

  function Finder(root) {
    this.root = root;
    root.__bcsf = this;
    this.pid = String(root.getAttribute('data-product-id') || '');
    this.handle = String(root.getAttribute('data-product-handle') || '');
    this.chart = String(root.getAttribute('data-chart') || '');
    this.optionName = String(root.getAttribute('data-option') || 'Size');
    this.offered = String(root.getAttribute('data-sizes') || '').split('|').map(norm).filter(Boolean);
    this.variantIds = String(root.getAttribute('data-variant-ids') || '').split(',').map(function (s) { return s.trim(); }).filter(Boolean);
    this.test = String(root.getAttribute('data-test') || '').slice(0, 40);
    this.dl = root.getAttribute('data-datalayer') !== 'off';
    this.list = BREEDS[this.chart] || [];
    this.id = 'bc-sf-' + (++uid);
    this.rec = null;          /* current recommendation (the one properties are built from) */
    this.breed = null;        /* selected breed entry */
    this.breedResult = null;  /* last breed result (shown in the breed pane) */
    this.measureResult = null;
    this.used = false;        /* shopper pressed "Use size" for the current recommendation */
    this.dirty = false;
    this.typed = '';
    this.lastCommit = '';
    this.queryTags = 0;
    this.lastRecSig = '';
    this.lastMeasureSig = '';
    this.lastAtc = 0;
    this.built = false;
    this.tab = 0;
    if (!CHARTS[this.chart] || !this.pid) { root.hidden = true; return; }
    this.findHost();
    if (!this.sizeGroup) { root.hidden = true; return; }
    this.restore();
    this.bindHost();
    this.toggle = root.querySelector('[data-bc-sf-toggle]');
    this.panel = root.querySelector('[data-bc-sf-panel]');
    if (!this.toggle || !this.panel) { root.hidden = true; return; }
    var self = this;
    this.toggle.addEventListener('click', function () { self.setOpen(self.toggle.getAttribute('aria-expanded') !== 'true', true); });
    root.hidden = false;
    this.updateToggle();
  }

  /* Find the existing size selector WITHOUT changing it: the size group inside the same .pdp-option as the snippet;
     fallback = the only [data-pdp-sizes] group on the page whose buttons match this product's size values. */
  Finder.prototype.findHost = function () {
    var r = this.root, doc = W.document;
    var sel = r.getAttribute('data-size-group') || '[data-pdp-sizes]';
    var opt = r.closest ? r.closest('.pdp-option') : null;
    var g = opt ? opt.querySelector(sel) : null;
    if (!g) {
      var all = Array.prototype.slice.call(doc.querySelectorAll(sel)), self = this;
      all = all.filter(function (x) { var b = self.sizeButtons(x); return b.length && b.every(function (bt) { return self.offered.indexOf(norm(bt.getAttribute('data-size'))) > -1; }); });
      if (all.length === 1) g = all[0];
    }
    this.sizeGroup = g;
    this.form = doc.querySelector(r.getAttribute('data-form') || '[data-pdp-form]');
    this.atcSel = r.getAttribute('data-atc') || '[data-pdp-atc]';
  };
  Finder.prototype.sizeButtons = function (g) {
    g = g || this.sizeGroup;
    return g ? Array.prototype.slice.call(g.querySelectorAll('.pdp-size[data-size], [data-size]')).filter(function (b) { return b.tagName === 'BUTTON' || b.getAttribute('role') === 'button' || b.tagName === 'INPUT'; }) : [];
  };
  Finder.prototype.sizeButton = function (size) {
    var want = norm(size);
    return this.sizeButtons().filter(function (b) { return norm(b.getAttribute('data-size')) === want; })[0] || null;
  };
  Finder.prototype.activeSize = function () {
    var a = this.sizeGroup ? this.sizeGroup.querySelector('.is-active[data-size], [aria-pressed="true"][data-size], [aria-checked="true"][data-size]') : null;
    return a ? norm(a.getAttribute('data-size')) : '';
  };
  Finder.prototype.variantId = function () {
    var i = this.form ? this.form.querySelector('[name="id"]') : null;
    return i ? String(i.value || '').trim() : '';
  };

  /* listeners on the host page: read-only, never preventDefault, never stopPropagation */
  Finder.prototype.bindHost = function () {
    var self = this, doc = W.document;
    doc.addEventListener('click', function (e) {
      var tgt = e.target && e.target.closest ? e.target : null;
      if (!tgt) return;
      var atc = tgt.closest(self.atcSel);
      if (atc && self.form && self.form.contains(atc) && !atc.disabled && atc.getAttribute('aria-disabled') !== 'true') self.onAddToCart('click');
      if (self.sizeGroup && self.sizeGroup.contains(tgt)) setTimeout(function () { self.syncProps(); self.refreshUseButtons(); }, 0);
      else if (tgt.closest('[data-pdp-swatches], .pdp-swatch')) setTimeout(function () { self.syncProps(); }, 0);
    }, true);
    doc.addEventListener('submit', function (e) { if (self.form && e.target === self.form) self.onAddToCart('submit'); }, true);
    W.addEventListener('pagehide', function () { self.commitSearch('pagehide'); });
    W.addEventListener('pageshow', function () { self.syncProps(); });
  };

  Finder.prototype.onAddToCart = function (via) {
    var attached = this.syncProps();
    if (!attached) return;
    var now = Date.now();
    if (now - this.lastAtc < 1500) return;
    this.lastAtc = now;
    var r = this.rec, act = this.activeSize();
    track('bc_sf_add_to_cart_with_rec', {
      bc_sf_chart: this.chart, bc_sf_rec_size: r.sizes.join('/'), bc_sf_match: act ? (r.sizes.indexOf(act) > -1 ? 'yes' : 'no') : '',
      bc_sf_from: r.from, bc_sf_used: this.used ? 'yes' : 'no'
    }, { dataLayer: this.dl });
  };

  /* hidden line-item properties: only on this product's form, only for this product's own recommendation */
  Finder.prototype.props = function () {
    var r = this.rec;
    if (!r || !r.sizes || !r.sizes.length || r.pid !== this.pid || r.chart !== this.chart) return null;
    var vid = this.variantId();
    if (!vid || this.variantIds.indexOf(vid) < 0) return null;
    var p = {
      _bc_sf_v: PROP_VERSION,
      _bc_sf_chart: this.chart,
      _bc_sf_method: r.method,
      _bc_sf_rec: r.sizes.join('/'),
      _bc_sf_borderline: r.borderline ? 'yes' : 'no',
      _bc_sf_kind: r.kind,
      _bc_sf_from: r.from
    };
    if (r.size) p._bc_sf_primary = r.size;
    if (r.breed) p._bc_sf_breed = String(r.breed).slice(0, 40);
    if (r.neck != null) p._bc_sf_neck_cm = fmtCm(r.neck);
    if (r.chest != null) p._bc_sf_chest_cm = fmtCm(r.chest);
    var act = this.activeSize();
    if (act) p._bc_sf_match = r.sizes.indexOf(act) > -1 ? 'yes' : 'no';
    p._bc_sf_used = this.used ? 'yes' : 'no';
    if (this.optionName && this.optionName.toLowerCase() !== 'size') p._bc_sf_option = this.optionName.slice(0, 40);
    if (this.test) p._bc_sf_test = this.test;
    return p;
  };
  Finder.prototype.syncProps = function () {
    var f = this.form;
    if (!f) return false;
    Array.prototype.slice.call(f.querySelectorAll('input[data-bc-sf-prop]')).forEach(function (n) { n.parentNode.removeChild(n); });
    var p = this.props();
    if (!p) return false;
    for (var k in p) {
      var i = W.document.createElement('input');
      i.type = 'hidden'; i.name = 'properties[' + k + ']'; i.value = p[k];
      i.setAttribute('data-bc-sf-prop', '');
      f.appendChild(i);
    }
    return true;
  };

  Finder.prototype.restore = function () {
    storePurge();
    var o = storeGet(this.pid, this.chart);
    if (!o) return;
    var r = o.rec;
    if (!r || !r.sizes || !r.sizes.every(function (s) { return ORDER.indexOf(s) > -1; })) return;
    r.from = 'restored'; r.pid = this.pid; r.chart = this.chart;
    this.rec = r;
    if (r.method === 'breed') this.breedResult = r; else this.measureResult = r;
    if (o.breed) this.breed = this.list.filter(function (e) { return e.n === o.breed; })[0] || null;
    this.syncProps();
  };
  Finder.prototype.save = function () {
    if (!this.rec) { storeSet(this.pid, this.chart, null); return; }
    var r = this.rec, keep = {};
    ['method', 'kind', 'size', 'sizes', 'borderline', 'breed', 'plural', 'neck', 'chest', 'alt', 'shape'].forEach(function (k) { if (r[k] != null) keep[k] = r[k]; });
    storeSet(this.pid, this.chart, { rec: keep, breed: this.breed ? this.breed.n : null });
  };

  Finder.prototype.setRec = function (r) {
    if (r && r.sizes && r.sizes.length) {
      r.from = 'this_page'; r.pid = this.pid; r.chart = this.chart;
      this.rec = r; this.used = false;
      var sig = r.method + '|' + r.sizes.join('/') + '|' + r.kind + '|' + (r.breed || '') + '|' + (r.neck || '') + '|' + (r.chest || '');
      if (sig !== this.lastRecSig) {
        this.lastRecSig = sig;
        track('bc_sf_recommendation', { bc_sf_chart: this.chart, bc_sf_method: r.method, bc_sf_rec_size: r.sizes.join('/'), bc_sf_borderline: r.borderline ? 'yes' : 'no', bc_sf_kind: r.kind }, { dataLayer: this.dl });
      }
    } else if (r === null) {
      this.rec = null; this.used = false; this.lastRecSig = '';
    }
    this.save();
    this.syncProps();
    this.updateToggle();
  };

  Finder.prototype.updateToggle = function () {
    var sub = this.toggle && this.toggle.querySelector('[data-bc-sf-toggle-sub]');
    if (!sub) return;
    if (!sub.__orig) sub.__orig = sub.textContent;
    sub.textContent = this.rec && this.rec.from === 'restored' ? t('toggleRestored', { sizes: list(this.rec.sizes) }) : sub.__orig;
  };

  Finder.prototype.setOpen = function (open, byUser) {
    this.toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (open) {
      if (!this.built) this.build();
      this.panel.hidden = false;
      this.root.classList.add('is-open');
      if (byUser && !OPENED[this.pid]) {
        OPENED[this.pid] = 1;
        track('bc_sf_open', { bc_sf_chart: this.chart, bc_sf_has_saved: this.rec && this.rec.from === 'restored' ? 'yes' : 'no' }, { dataLayer: this.dl });
      }
      focusEl(this.titleEl);
    } else {
      this.commitSearch('close');
      this.closeList();
      this.panel.hidden = true;
      this.root.classList.remove('is-open');
      if (byUser !== false) focusEl(this.toggle);
    }
  };

  Finder.prototype.build = function () {
    var self = this, id = this.id;
    this.built = true;
    this.panel.innerHTML = '';
    this.panel.setAttribute('role', 'region');
    this.panel.setAttribute('aria-labelledby', id + '-title');
    this.titleEl = h('p', { class: 'bc-sf__title', id: id + '-title', tabindex: '-1', text: t('title') });
    var closeBtn = h('button', { type: 'button', class: 'bc-sf__close', 'aria-label': t('closeLabel'), text: t('close'), onclick: function () { self.setOpen(false, true); } });
    var head = h('div', { class: 'bc-sf__head' }, [this.titleEl, closeBtn]);

    this.tabs = [
      h('button', { type: 'button', role: 'tab', id: id + '-tab-b', 'aria-controls': id + '-pane-b', class: 'bc-sf__tab', text: t('tabBreed') }),
      h('button', { type: 'button', role: 'tab', id: id + '-tab-m', 'aria-controls': id + '-pane-m', class: 'bc-sf__tab', text: t('tabMeasure') })
    ];
    var tablist = h('div', { class: 'bc-sf__tabs', role: 'tablist', 'aria-label': t('tablist') }, this.tabs);
    this.tabs.forEach(function (b, i) { b.addEventListener('click', function () { self.selectTab(i, false); }); });
    tablist.addEventListener('keydown', function (e) {
      var k = e.key, i = self.tab;
      if (k === 'ArrowRight' || k === 'ArrowLeft') i = (i + 1) % 2;
      else if (k === 'Home') i = 0;
      else if (k === 'End') i = 1;
      else return;
      e.preventDefault(); self.selectTab(i, false); focusEl(self.tabs[i]);
    });

    /* breed pane */
    this.input = h('input', {
      id: id + '-breed', type: 'text', class: 'bc-sf__input', role: 'combobox', 'aria-autocomplete': 'list', 'aria-expanded': 'false',
      'aria-controls': id + '-list', 'aria-describedby': id + '-breed-hint ' + id + '-msg', autocomplete: 'off', autocapitalize: 'words',
      spellcheck: 'false', maxlength: '60', placeholder: t('breedPlaceholder'), enterkeyhint: 'search'
    });
    this.listbox = h('ul', { id: id + '-list', role: 'listbox', class: 'bc-sf__list', 'aria-label': t('listLabel'), hidden: true });
    this.msg = h('div', { id: id + '-msg', class: 'bc-sf__msg' });
    var chips = (BREEDS.chips && BREEDS.chips[this.chart]) || [];
    var chipWrap = chips.length ? h('div', { class: 'bc-sf__chips', role: 'group', 'aria-label': t('popular') }, chips.map(function (name) {
      var e = self.list.filter(function (x) { return x.n === name; })[0];
      if (!e) return null;
      return h('button', { type: 'button', class: 'bc-sf__chip', text: name, onclick: function () { self.pickBreed(e, 'chip'); } });
    })) : null;
    var measureInstead = h('button', { type: 'button', class: 'bc-sf__link', text: t('measureInstead'), onclick: function () { self.goMeasure(); } });
    this.breedOut = h('div', { class: 'bc-sf__result', 'data-bc-sf-result': 'breed' });
    this.paneB = h('div', { role: 'tabpanel', id: id + '-pane-b', 'aria-labelledby': id + '-tab-b', class: 'bc-sf__pane' }, [
      h('label', { for: id + '-breed', class: 'bc-sf__label', text: t('breedLabel') }),
      h('div', { class: 'bc-sf__combo' }, [this.input, this.listbox]),
      h('p', { id: id + '-breed-hint', class: 'bc-sf__hint', text: t('breedHint') }),
      this.msg, this.breedOut, chipWrap, measureInstead
    ]);
    this.input.addEventListener('input', function () { self.onInput(); });
    this.input.addEventListener('keydown', function (e) { self.onKey(e); });
    this.input.addEventListener('blur', function (e) {
      var to = e.relatedTarget;
      if (to && self.listbox.contains(to)) return;
      setTimeout(function () { if (W.document.activeElement !== self.input) { self.closeList(); self.commitSearch('blur'); } }, 0);
    });

    /* measure pane */
    this.fields = {};
    var mkField = function (dim) {
      var inp = h('input', { id: id + '-' + dim, name: dim, type: 'text', inputmode: 'decimal', class: 'bc-sf__input bc-sf__input--num', autocomplete: 'off', 'aria-describedby': id + '-' + dim + '-help ' + id + '-' + dim + '-err', maxlength: '8' });
      var err = h('p', { id: id + '-' + dim + '-err', class: 'bc-sf__error', hidden: true });
      self.fields[dim] = { input: inp, err: err };
      return h('div', { class: 'bc-sf__field' }, [
        h('label', { for: id + '-' + dim, class: 'bc-sf__label', text: t(dim) }),
        h('p', { id: id + '-' + dim + '-help', class: 'bc-sf__hint', text: t(dim + 'Help') }),
        h('div', { class: 'bc-sf__unit-wrap' }, [inp, h('span', { class: 'bc-sf__unit', 'aria-hidden': 'true', text: 'cm' })]),
        err
      ]);
    };
    var how = h('details', { class: 'bc-sf__how' }, [h('summary', { text: t('howTitle') }), h('ol', {}, T.how.map(function (s) { return h('li', { text: s }); }))]);
    this.form2 = h('form', { class: 'bc-sf__form', novalidate: true }, [
      h('fieldset', { class: 'bc-sf__fieldset' }, [h('legend', { class: 'bc-sf__legend', text: t('legend') }), mkField('chest'), mkField('neck')]),
      how,
      h('button', { type: 'submit', class: 'bc-sf__btn bc-sf__btn--primary', text: t('show') })
    ]);
    this.form2.addEventListener('submit', function (e) { e.preventDefault(); e.stopPropagation(); self.onMeasure(); });
    this.measureOut = h('div', { class: 'bc-sf__result', 'data-bc-sf-result': 'measure' });
    this.paneM = h('div', { role: 'tabpanel', id: id + '-pane-m', 'aria-labelledby': id + '-tab-m', class: 'bc-sf__pane', hidden: true }, [this.form2, this.measureOut]);

    this.status = h('p', { class: 'bc-sf__sr', role: 'status', 'aria-live': 'polite' });
    this.panel.appendChild(head);
    this.panel.appendChild(tablist);
    this.panel.appendChild(this.paneB);
    this.panel.appendChild(this.paneM);
    this.panel.appendChild(this.status);
    this.panel.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' && e.key !== 'Esc') return;
      if (!self.listbox.hidden) { self.closeList(); e.preventDefault(); return; }
      e.preventDefault();
      self.setOpen(false, true);
    });

    /* restored answer: show it, never select anything */
    var startTab = this.rec && this.rec.method === 'measure' ? 1 : 0;
    if (this.breed && this.rec && this.rec.method === 'breed') this.input.value = this.breed.n;
    if (this.measureResult) { this.fields.chest.input.value = fmtCm(this.measureResult.chest); this.fields.neck.input.value = fmtCm(this.measureResult.neck); }
    this.selectTab(startTab, false, true);
  };

  Finder.prototype.selectTab = function (i, focus, silent) {
    if (i !== this.tab) this.commitSearch('tab');
    this.tab = i;
    this.tabs.forEach(function (b, k) { b.setAttribute('aria-selected', k === i ? 'true' : 'false'); b.tabIndex = k === i ? 0 : -1; });
    this.paneB.hidden = i !== 0;
    this.paneM.hidden = i !== 1;
    this.closeList();
    if (i === 0) this.renderBreed(); else this.renderMeasure();
    if (focus) {
      if (i === 1) focusEl(coarse() ? this.tabs[1] : this.fields.chest.input);
      else focusEl(coarse() ? this.tabs[0] : this.input);
    }
  };
  Finder.prototype.goMeasure = function () { this.commitSearch('measure_instead'); this.selectTab(1, true); };

  /* ---------- breed combobox ---------- */
  Finder.prototype.onInput = function () {
    this.dirty = true;
    this.typed = this.input.value;
    if (this.breed && this.input.value !== this.breed.n) {
      this.breed = null;
      this.breedResult = null;
      if (this.rec && this.rec.method === 'breed') this.setRec(null);
      this.renderBreed();
    }
    this.openList();
  };
  Finder.prototype.openList = function () {
    var self = this, q = this.input.value, cls = classify(this.list, q);
    this.listbox.innerHTML = '';
    this.msg.innerHTML = '';
    this.items = [];
    this.active = -1;
    this.input.removeAttribute('aria-activedescendant');
    if (cls.result === 'empty') { this.closeList(); return; }
    if (cls.result === 'incomplete' && cls.reason === 'short' && !cls.matches.length) {
      this.msg.appendChild(h('p', { class: 'bc-sf__hint', text: t('keepTyping') }));
      this.closeList();
      return;
    }
    if (!cls.matches.length) {
      this.closeList();
      this.msg.appendChild(h('p', { class: 'bc-sf__nomatch', text: t('noMatch', { q: q.trim().slice(0, 40) }) }));
      this.msg.appendChild(h('button', { type: 'button', class: 'bc-sf__btn', text: t('noMatchBtn'), onclick: function () { self.goMeasure(); } }));
      return;
    }
    cls.matches.forEach(function (m, i) {
      var sub = null;
      if (m.via === 'alias' && fold(m.term) !== fold(m.e.n)) sub = t('alsoCalled', { term: m.term });
      else if (m.via === 'typo' || (m.tier === 'full' && m.s < 66)) sub = t('didYouMean');
      var pill = m.e.g === 0 || !(m.e.s || []).length ? t('measurePill') : list(m.e.s);
      var li = h('li', { id: self.id + '-o' + i, role: 'option', class: 'bc-sf__opt', 'aria-selected': 'false' }, [
        h('span', { class: 'bc-sf__opt-main' }, [h('span', { class: 'bc-sf__opt-name', text: m.e.n }), sub ? h('span', { class: 'bc-sf__opt-sub', text: sub }) : null]),
        h('span', { class: 'bc-sf__pill', text: pill })
      ]);
      li.addEventListener('mousedown', function (ev) { ev.preventDefault(); });
      li.addEventListener('click', function () { self.pickBreed(m.e, 'list'); });
      self.listbox.appendChild(li);
      self.items.push(m.e);
    });
    this.listbox.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
  };
  Finder.prototype.closeList = function () {
    if (!this.listbox) return;
    this.listbox.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
    this.active = -1;
  };
  Finder.prototype.setActive = function (i) {
    var opts = this.listbox.querySelectorAll('[role="option"]');
    if (!opts.length) return;
    this.active = (i + opts.length) % opts.length;
    var self = this;
    Array.prototype.forEach.call(opts, function (o, k) { o.setAttribute('aria-selected', k === self.active ? 'true' : 'false'); o.classList.toggle('is-active', k === self.active); });
    this.input.setAttribute('aria-activedescendant', opts[this.active].id);
    try { opts[this.active].scrollIntoView({ block: 'nearest' }); } catch (e) { /* ignore */ }
  };
  Finder.prototype.onKey = function (e) {
    var open = !this.listbox.hidden;
    if (e.key === 'ArrowDown') { e.preventDefault(); if (!open) this.openList(); this.setActive(this.active + 1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); if (open) this.setActive(this.active - 1); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      if (open && this.items && this.items.length) this.pickBreed(this.items[this.active > -1 ? this.active : 0], 'list');
      else this.commitSearch('enter');
    } else if (e.key === 'Escape' || e.key === 'Esc') {
      if (open) { e.preventDefault(); e.stopPropagation(); this.closeList(); }
    }
  };

  /* bc_sf_search fires once per committed query: on choosing a breed, Enter, leaving the field, switching to measuring,
     closing the finder or leaving the page. Keystrokes and typing pauses never fire it. */
  Finder.prototype.commitSearch = function (trigger, picked) {
    if (!this.dirty) return;
    this.dirty = false;
    var raw = this.typed;
    var cls = picked ? { result: 'match' } : classify(this.list, raw);
    if (cls.result === 'empty') return;
    var clean = sanitizeQuery(raw);
    var key = clean + '|' + cls.result;
    if (key === this.lastCommit) return;
    this.lastCommit = key;
    track('bc_sf_search', { bc_sf_chart: this.chart, bc_sf_result: cls.result, bc_sf_trigger: trigger }, { dataLayer: this.dl });
    if (cls.result === 'no_match' || cls.result === 'incomplete') {
      var tags = { bc_sf_chart: this.chart };
      if (this.queryTags < MAX_QUERY_TAGS && clean) { tags.bc_sf_query = clean; this.queryTags++; }
      if (cls.result === 'incomplete') tags.bc_sf_incomplete_reason = cls.reason;
      track(cls.result === 'no_match' ? 'bc_sf_no_match' : 'bc_sf_incomplete', tags, { dataLayer: this.dl });
    }
  };

  Finder.prototype.pickBreed = function (e, via) {
    if (!e) return;
    if (via === 'list') this.commitSearch('select', true);
    else this.commitSearch('chip');
    this.closeList();
    this.breed = e;
    this.input.value = e.n;
    this.typed = e.n;
    this.dirty = false;
    this.msg.innerHTML = '';
    track('bc_sf_breed_selected', { bc_sf_chart: this.chart, bc_sf_breed: e.n, bc_sf_breed_via: via, bc_sf_breed_guide: e.g === 0 ? 'none' : ((e.s || []).length ? 'yes' : 'measure_only') }, { dataLayer: this.dl });
    var r = breedRec(this.chart, e, this.offered);
    this.breedResult = r;
    if (r && r.sizes.length) this.setRec(r);
    else if (this.rec && this.rec.method === 'breed') this.setRec(null);
    else this.save();
    this.renderBreed();
    this.say(r && r.sizes.length ? t('resultStatus', { sizes: list(r.sizes) }) : (r ? this.breedNoSizeText(r) : ''));
  };
  Finder.prototype.breedNoSizeText = function (r) { return r.kind === 'no_guide' ? t('noGuide', { plural: r.plural }) : t('measureOnly', { plural: r.plural }); };

  Finder.prototype.renderBreed = function () {
    var out = this.breedOut, r = this.breedResult, self = this;
    if (!out) return;
    out.innerHTML = '';
    if (!r) return;
    var box = h('div', { class: 'bc-sf__card' + (r.from === 'restored' ? ' is-restored' : '') });
    if (r.from === 'restored') box.appendChild(h('p', { class: 'bc-sf__eyebrow', text: t('restored') }));
    if (!r.sizes.length) {
      box.appendChild(h('p', { class: 'bc-sf__text', text: this.breedNoSizeText(r) }));
      box.appendChild(h('button', { type: 'button', class: 'bc-sf__btn bc-sf__btn--primary', text: t('measureMyDog'), onclick: function () { self.goMeasure(); } }));
      out.appendChild(box);
      return;
    }
    box.appendChild(h('p', { class: 'bc-sf__label-sm', text: t('suggested') }));
    box.appendChild(h('p', { class: 'bc-sf__size', text: r.sizes.length > 1 ? t('sizeOr', { a: r.sizes[0], b: r.sizes[1] }) : r.sizes[0] }));
    var lines = [];
    if (r.substituted) {
      var range = r.substituted.dir === 'up' ? t('startsAt', { size: r.sizes[0] }) : t('goesTo', { size: r.sizes[0] });
      lines.push(t('breedNotOffered', { plural: r.plural, sizes: list(r.substituted.from.split('/')), range: range, size: r.sizes[0] }));
    } else {
      lines.push(t('mostWear', { plural: r.plural, sizes: list(r.sizes) }));
      if (r.dropped && r.dropped.length) {
        var order = sizesOf(this.chart);
        lines.push(order.indexOf(r.dropped[0]) < order.indexOf(r.sizes[0]) ? t('startsAt', { size: this.offered[0] }) : t('goesTo', { size: this.offered[this.offered.length - 1] }));
      }
      if (r.sizes.length > 1) lines.push(t('chooseBetween'));
      if (r.tip) lines.push(r.tip);
      if (r.conf === 'L') lines.push(t('lowConf'));
    }
    box.appendChild(h('p', { class: 'bc-sf__text', text: lines.join(' ') }));
    box.appendChild(this.useButtons(r.sizes, r.sizes.length === 1 ? r.sizes[0] : null));
    box.appendChild(h('p', { class: 'bc-sf__text bc-sf__text--muted', text: t('measurePrompt') }));
    box.appendChild(h('button', { type: 'button', class: 'bc-sf__btn', text: t('measureBtn'), onclick: function () { self.goMeasure(); } }));
    out.appendChild(box);
  };

  /* ---------- measurements ---------- */
  Finder.prototype.onMeasure = function () {
    var self = this, vals = {}, firstBad = null;
    ['chest', 'neck'].forEach(function (dim) {
      var f = self.fields[dim], c = checkMeasure(dim, f.input.value);
      if (c.ok) { vals[dim] = c.v; f.input.removeAttribute('aria-invalid'); f.err.hidden = true; f.err.textContent = ''; return; }
      var msg = c.err === 'empty' ? T.errEmpty[dim] : (c.err === 'range' ? T.errRange[dim] : T.errNan);
      f.input.setAttribute('aria-invalid', 'true');
      f.err.textContent = msg; f.err.hidden = false;
      if (!firstBad) firstBad = f.input;
    });
    if (firstBad) { this.say(t('errSummary')); focusEl(firstBad); return; }
    var r = measure(this.chart, vals.neck, vals.chest, this.offered);
    if (!r) return;
    if (this.breed) r.breed = this.breed.n;
    var sig = vals.neck + '|' + vals.chest;
    if (sig !== this.lastMeasureSig) {
      this.lastMeasureSig = sig;
      track('bc_sf_measure_entered', { bc_sf_chart: this.chart, bc_sf_with_breed: this.breed ? 'yes' : 'no' }, { dataLayer: this.dl });
    }
    this.measureResult = r;
    this.setRec(r);
    this.renderMeasure();
    this.say(r.borderline ? t('betweenTitle', { a: r.sizes[0], b: r.sizes[1] }) + '. ' + t('resultStatus', { sizes: r.size }) : t('resultStatus', { sizes: r.size }));
  };

  Finder.prototype.renderMeasure = function () {
    var out = this.measureOut, r = this.measureResult;
    if (!out) return;
    out.innerHTML = '';
    if (!r) return;
    var box = h('div', { class: 'bc-sf__card' + (r.borderline ? ' is-borderline' : '') + (r.from === 'restored' ? ' is-restored' : '') });
    if (r.from === 'restored') box.appendChild(h('p', { class: 'bc-sf__eyebrow', text: t('restored') }));
    var rows = r.ranges || {};
    var rangeText = function (s) { var x = rows[s] || (CHARTS[r.chart].sizes.filter(function (y) { return y.size === s; })[0] || {}).adj; return x ? t('rangeLine', { size: s, chest: cmRange(x.chest), neck: cmRange(x.neck) }) : ''; };
    if (r.kind === 'between') {
      box.appendChild(h('p', { class: 'bc-sf__heading', text: t('betweenTitle', { a: r.sizes[0], b: r.sizes[1] }) }));
      box.appendChild(h('p', { class: 'bc-sf__label-sm', text: t('suggested') }));
      box.appendChild(h('p', { class: 'bc-sf__size', text: r.size }));
      box.appendChild(h('p', { class: 'bc-sf__text', text: t('betweenText', { size: r.size }) }));
      box.appendChild(h('ul', { class: 'bc-sf__ranges' }, r.sizes.map(function (s) { return h('li', { text: rangeText(s) }); })));
      box.appendChild(this.useButtons([r.size, r.alt], r.size));
    } else if (r.kind === 'closest' || r.kind === 'help') {
      box.appendChild(h('p', { class: 'bc-sf__label-sm', text: t('closestTitle') }));
      box.appendChild(h('p', { class: 'bc-sf__size', text: r.size }));
      var cl = [];
      if (r.mismatch) cl.push(t('mismatch'));
      if (r.shape === 'deep_chest') cl.push(t('deepChest'));
      cl.push(t('closestText'));
      box.appendChild(h('p', { class: 'bc-sf__text', text: cl.join(' ') }));
      box.appendChild(h('ul', { class: 'bc-sf__ranges' }, [h('li', { text: rangeText(r.size) })]));
      box.appendChild(this.useButtons([r.size], r.size));
    } else {
      box.appendChild(h('p', { class: 'bc-sf__label-sm', text: t('suggested') }));
      box.appendChild(h('p', { class: 'bc-sf__size', text: r.size }));
      var lines = [];
      if (r.kind === 'check') lines.push(t('checkText', { size: r.size }));
      if (r.shape === 'deep_chest') lines.push(t('deepChest'));
      if (r.substituted) lines.push(r.substituted.dir === 'up' ? t('startsAt', { size: r.size }) : t('goesTo', { size: r.size }));
      var x = rows[r.size];
      if (x) lines.push(t('fitText', { size: r.size, chest: cmRange(x.chest), neck: cmRange(x.neck) }));
      box.appendChild(h('p', { class: 'bc-sf__text', text: lines.join(' ') }));
      box.appendChild(this.useButtons([r.size], r.size));
    }
    out.appendChild(box);
  };

  Finder.prototype.useButtons = function (sizes, primary) {
    var self = this, wrap = h('div', { class: 'bc-sf__use' });
    sizes.forEach(function (s) {
      var target = self.sizeButton(s);
      if (!target) return;
      var sold = target.classList.contains('pdp-size--sold-out');
      var b = h('button', { type: 'button', class: 'bc-sf__btn' + (s === primary ? ' bc-sf__btn--primary' : ''), 'data-bc-sf-use': s }, [
        h('span', { 'data-label': '', text: t('useSize', { size: s }) }),
        sold ? h('span', { class: 'bc-sf__muted', text: ' ' + t('soldOut') }) : null
      ]);
      b.addEventListener('click', function () { self.useSize(s); });
      wrap.appendChild(b);
    });
    setTimeout(function () { self.refreshUseButtons(); }, 0);
    return wrap;
  };
  Finder.prototype.refreshUseButtons = function () {
    if (!this.panel) return;
    var act = this.activeSize();
    Array.prototype.forEach.call(this.panel.querySelectorAll('[data-bc-sf-use]'), function (b) {
      var s = b.getAttribute('data-bc-sf-use'), on = s === act, lab = b.querySelector('[data-label]');
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
      if (lab) lab.textContent = on ? t('selected', { size: s }) : t('useSize', { size: s });
    });
  };
  /* the shopper's own action: clicks the existing size button exactly as a tap would */
  Finder.prototype.useSize = function (s) {
    var target = this.sizeButton(s);
    if (!target || !this.rec) return;
    target.click();
    this.used = true;
    track('bc_sf_size_selected_from_rec', { bc_sf_chart: this.chart, bc_sf_size: s, bc_sf_method: this.rec.method, bc_sf_from: this.rec.from, bc_sf_borderline: this.rec.borderline ? 'yes' : 'no' }, { dataLayer: this.dl });
    var self = this;
    setTimeout(function () { self.syncProps(); self.refreshUseButtons(); }, 0);
    this.say(t('selectedStatus', { size: s }));
  };

  Finder.prototype.say = function (msg) {
    var s = this.status;
    if (!s) return;
    s.textContent = '';
    setTimeout(function () { s.textContent = msg; }, 30);
  };

  function initAll() {
    if (!W || !W.document) return;
    Array.prototype.forEach.call(W.document.querySelectorAll('[data-bc-sf]'), function (el) { if (!el.__bcsf) new Finder(el); });
  }

  return {
    version: VERSION,
    initAll: initAll,
    Finder: Finder,
    _log: LOG,
    engine: {
      CHARTS: CHARTS, BREEDS: BREEDS, TOL: TOL, GAP: GAP, PLAUS: PLAUS, T: T,
      fold: fold, prep: prep, search: search, classify: classify, sanitizeQuery: sanitizeQuery,
      measure: measure, baseRule: baseRule, breedRec: breedRec, parseCm: parseCm, checkMeasure: checkMeasure
    }
  };
});
